import { useState, useMemo, useCallback } from 'react';
import { AppBar, Toolbar, Box, IconButton, Drawer, List, ListItem, ListItemText, useMediaQuery, Button, Typography } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import { Link as RouterLink, NavLink, useLocation } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import ThemeToggle from '../ThemeToggle';

const navLinks = [
  { label: 'Home', to: '/' },
  { label: 'Services', to: '/services' },
  { label: 'Contact', to: '/contact' },
  { label: 'JoinUs', to: '/join' },
];

const Header = () => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();

  // Memoized styles to prevent re-renders
  const logoContainerStyles = useMemo(() => ({
    width: '180px',
    height: '55px',
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '&:hover': {
      opacity: 0.9,
    }
  }), []);

  const appBarStyles = useMemo(() => ({
    color: 'primary.main',
    boxShadow: theme.palette.mode === 'dark' 
      ? '0 4px 20px 0 rgba(0,0,0,0.3)' 
      : '0 4px 20px 0 rgba(0,0,0,0.1)',
    borderRadius: '0 0 16px 16px',
    margin: '16px 16px 0',
    width: 'calc(100% - 32px)',
    maxWidth: '100%',
    zIndex: 1200,
    backdropFilter: 'blur(10px)',
    backgroundColor: theme.palette.mode === 'dark' 
      ? 'rgba(30, 30, 30, 0.95)' 
      : 'rgba(255, 255, 255, 0.95)',
    transition: 'all 0.3s ease-in-out',
    '&:hover': {
      boxShadow: theme.palette.mode === 'dark' 
        ? '0 8px 30px 0 rgba(0,0,0,0.4)' 
        : '0 8px 30px 0 rgba(0,0,0,0.15)'
    }
  }), [theme.palette.mode]);

  const handleDrawerToggle = useCallback(() => {
    setDrawerOpen(!drawerOpen);
  }, [drawerOpen]);

  const handleDrawerClose = useCallback(() => {
    setDrawerOpen(false);
  }, []);

  // Keyboard navigation handler
  const handleKeyDown = useCallback((event) => {
    if (event.key === 'Escape') {
      setDrawerOpen(false);
    }
  }, []);

  const drawer = (
    <Box 
      sx={{ 
        width: 280, 
        height: '100%',
        bgcolor: 'background.paper',
        display: 'flex',
        flexDirection: 'column'
      }} 
      role="navigation"
      aria-label="Mobile navigation menu"
      onKeyDown={handleKeyDown}
    >
      {/* Header with logo, close button and theme toggle */}
      <Box sx={{ 
        p: 2, 
        borderBottom: 1, 
        borderColor: 'divider',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main' }}>
          PolicyTap
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ThemeToggle />
          <IconButton
            onClick={handleDrawerClose}
            aria-label="Close navigation menu"
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>
      
      {/* Navigation Links */}
      <Box component="nav" role="menubar" sx={{ flex: 1, pt: 2 }}>
        <List>
          {navLinks.map((item) => (
            <ListItem 
              key={item.to}
              component={NavLink} 
              to={item.to} 
              onClick={handleDrawerClose}
              role="menuitem"
              aria-label={`Navigate to ${item.label}`}
              sx={{ 
                mb: 1,
                mx: 2,
                borderRadius: 2,
                position: 'relative',
                transition: 'all 0.3s ease',
                textDecoration: 'none',
                bgcolor: 'rgba(25, 118, 210, 0.05)',
                border: '1px solid rgba(25, 118, 210, 0.1)',
                color: 'primary.main',
                '&:hover': {
                  bgcolor: 'rgba(25, 118, 210, 0.1)',
                  transform: 'translateX(4px)',
                  boxShadow: '0 2px 8px rgba(25, 118, 210, 0.15)',
                  borderColor: 'rgba(25, 118, 210, 0.2)'
                },
                '&.active': {
                  bgcolor: 'primary.main',
                  color: 'white',
                  boxShadow: '0 2px 8px rgba(25, 118, 210, 0.2)',
                  border: '1px solid transparent',
                  '&:before': {
                    content: '""',
                    position: 'absolute',
                    left: 0,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    width: '4px',
                    height: '60%',
                    bgcolor: 'white',
                    borderRadius: '0 2px 2px 0'
                  },
                  '&:after': {
                    content: '""',
                    position: 'absolute',
                    right: 16,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    width: '8px',
                    height: '8px',
                    bgcolor: 'white',
                    borderRadius: '50%'
                  },
                  '&:hover': {
                    bgcolor: 'primary.main',
                    transform: 'translateX(4px)'
                  }
                },
                '&:focus-visible': {
                  outline: '2px solid',
                  outlineColor: 'primary.main',
                  outlineOffset: '2px'
                }
              }}
            >
              <ListItemText 
                primary={item.label} 
                sx={{ 
                  '& .MuiListItemText-primary': {
                    fontWeight: 600,
                    fontSize: '1.1rem',
                    letterSpacing: '0.5px'
                  }
                }}
              />
            </ListItem>
          ))}
        </List>
      </Box>
    </Box>
  );

  return (
    <AppBar 
      component="header"
      position="fixed"
      elevation={4}
      sx={appBarStyles}
    >
      <Toolbar>
        <Box
          component={RouterLink}
          to="/"
          sx={{
            flexGrow: 1,
            display: 'flex',
            alignItems: 'center',
            textDecoration: 'none',
            '&:hover': {
              opacity: 0.8,
            },
          }}
          aria-label="PolicyTap homepage"
        >
          <Box sx={logoContainerStyles}>
            <Box
              component="img"
              src={theme.palette.mode === 'dark' ? "/logo512_trans_white.png" : "/logo512_trans.png"}
              alt="PolicyTap - Insurance Agent & Financial Advisor"
              key={theme.palette.mode}
              sx={{
                width: '100%',
                height: 'auto',
                objectFit: 'cover',
                objectPosition: 'center',
                transform: 'scale(0.9)',
                cursor: 'pointer',
                transition: 'opacity 0.3s ease-in-out'
              }}
              onClick={(e) => {
                e.preventDefault();
                window.location.href = '/';
              }}
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.parentElement.textContent = 'PolicyTap';
                e.target.parentElement.style.color = theme.palette.mode === 'dark' ? 'white' : theme.palette.primary.main;
                e.target.parentElement.style.fontWeight = 'bold';
                e.target.parentElement.style.fontSize = '1.25rem';
              }}
            />
          </Box>
        </Box>
        {isMobile ? (
          <>
            <IconButton
              color="primary"
              edge="end"
              aria-label="Open navigation menu"
              aria-expanded={drawerOpen}
              aria-controls="mobile-navigation"
              onClick={handleDrawerToggle}
              sx={{ ml: 1 }}
            >
              <MenuIcon />
            </IconButton>
            <Drawer
              id="mobile-navigation"
              anchor="right"
              open={drawerOpen}
              onClose={handleDrawerClose}
              ModalProps={{
                keepMounted: true, // Better open performance on mobile
              }}
            >
              {drawer}
            </Drawer>
          </>
        ) : (
          <Box 
            component="nav" 
            role="menubar"
            aria-label="Main navigation"
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            {navLinks.map((item) => (
              <Button
                key={item.to}
                color="primary"
                component={NavLink}
                to={item.to}
                role="menuitem"
                aria-label={`Navigate to ${item.label}`}
                aria-current={location.pathname === item.to ? 'page' : undefined}
                sx={{
                  fontSize: '1rem',
                  fontWeight: 600,
                  textTransform: 'none',
                  letterSpacing: '0.5px',
                  color: 'primary.main',
                  backgroundColor: 'transparent',
                  borderRadius: '8px',
                  padding: '8px 18px',
                  margin: '0 4px',
                  position: 'relative',
                  overflow: 'hidden',
                  '&:before': {
                    content: '""',
                    position: 'absolute',
                    bottom: 0,
                    left: '50%',
                    width: 0,
                    height: '2px',
                    backgroundColor: 'primary.main',
                    transition: 'all 0.3s ease-in-out',
                    transform: 'translateX(-50%)',
                  },
                  '&:hover': {
                    backgroundColor: theme.palette.mode === 'dark' 
                      ? 'rgba(255, 255, 255, 0.08)' 
                      : 'rgba(25, 118, 210, 0.04)',
                    transform: 'translateY(-1px)',
                    boxShadow: theme.palette.mode === 'dark' 
                      ? '0 2px 8px rgba(255, 255, 255, 0.1)' 
                      : '0 2px 8px rgba(25, 118, 210, 0.1)',
                    '&:before': {
                      width: '60%',
                    }
                  },
                  '&:focus-visible': {
                    backgroundColor: theme.palette.mode === 'dark' 
                      ? 'rgba(255, 255, 255, 0.12)' 
                      : 'rgba(25, 118, 210, 0.08)',
                    outline: '2px solid',
                    outlineColor: 'primary.main',
                    outlineOffset: '2px'
                  },
                  '&.active': {
                    color: '#0d47a1',
                    '&:before': {
                      width: '60%',
                      backgroundColor: '#0d47a1',
                    }
                  },
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                }}
              >
                {item.label}
              </Button>
            ))}
            <ThemeToggle />
          </Box>
        )}
      </Toolbar>
    </AppBar>
  );
};

export default Header;
